/**
 * Le fournisseur d'identite d'un domaine, s'il en a un (PCG-304).
 *
 * ── POURQUOI CE FICHIER EXISTE, ET POURQUOI IL EST ASYNCHRONE DES LE PREMIER JOUR
 *
 * **Cognito ne sait pas repondre a cette question depuis un navigateur.** Il n'existe aucune
 * API publique « ce domaine a-t-il un fournisseur » : `ListIdentityProviders` et
 * `DescribeIdentityProvider` sont des appels d'administration signes IAM, hors d'atteinte
 * d'un front. Ce qui s'en rapproche est `IdpIdentifiers` sur le fournisseur, avec
 * `?idp_identifier=` sur l'interface hebergee, mais son « non » est une page d'erreur de
 * Cognito, en anglais et hors de notre charte : elle rendrait un refus qu'on ne peut ni
 * traduire ni formuler.
 *
 * La reponse vit donc ici. **La signature est asynchrone alors que la lecture est locale**,
 * et c'est le seul point qui compte pour la suite : le jour ou une route de decouverte
 * existera cote API (le precedent exact est `/auth/passwordless`, deja publique et sans
 * authorizer), ce fichier change et **aucun appelant ne bouge**.
 *
 * ── CE QUE CETTE TABLE PROMET, ET CE QU'ELLE NE PROMET PAS
 *
 * Elle est juste **tant qu'on la tient**. Un fournisseur ajoute cote Cognito n'existe pas
 * ici tant que le front n'est pas redeploye, et ce silence ne se voit pas : personne ne
 * recoit d'erreur, la personne se voit simplement dire que son domaine n'a pas de SSO. C'est
 * le cout assume de ne pas ouvrir une route pour un seul fournisseur, et c'est la raison
 * pour laquelle le remplacement doit rester bon marche.
 *
 * ── ET UN FAIT A NE PAS ENTERRER
 *
 * **Avec un seul fournisseur, cette fonction ne decouvre rien : elle verifie une
 * appartenance.** Le parcours reste honnete parce qu'il ne promet rien d'autre, mais l'ecran
 * ne doit pas laisser croire qu'il interroge un annuaire. C'est aussi pourquoi « SSO » ne
 * doit jamais devenir un synonyme d'« interne » ailleurs dans le code : c'est vrai
 * aujourd'hui et faux au premier client qui apporte son propre fournisseur.
 */

/**
 * Le nom du fournisseur declare dans Cognito.
 *
 * **Attention, C'EST UN CONTRAT AVEC `inside-api`**, ou il vit dans
 * `services/ressources-setup/resources/cognito.yaml` sous `ProviderName`. Le renommer
 * la-bas casse ce bouton, et rien ici ne le signalera : Amplify enverra le navigateur vers
 * un fournisseur qui n'existe pas.
 */
const FREELANCE_PROVIDER = 'azure-entra-id-freelancecom';

/**
 * Les domaines qui entrent par le SSO Freelance.com.
 *
 * Une entree par domaine plutot qu'un test sur un suffixe : un suffixe ferait passer
 * `pas-freelance.com` et l'erreur ne se verrait qu'a la redirection.
 */
const PROVIDER_BY_DOMAIN: Record<string, string> = {
    'freelance.com': FREELANCE_PROVIDER,
};

/**
 * Le domaine d'une adresse, ou `null` si elle n'en porte pas.
 *
 * **Seul le DOMAINE voyage**, jamais l'adresse : c'est ce qui rend la question publiable le
 * jour ou elle partira sur le reseau, et c'est ce que fait Cloudflare. La reponse porte sur
 * une entreprise et non sur une personne, donc elle ne dit pas si un compte existe.
 */
const domainOf = (email: string): string | null => {
    const at = email.lastIndexOf('@');
    if (at < 1 || at === email.length - 1) {
        return null;
    }
    return email
        .slice(at + 1)
        .trim()
        .toLowerCase();
};

/**
 * Le fournisseur d'identite de cette adresse, ou `null` si elle n'en a pas.
 *
 * Asynchrone par anticipation, voir l'en-tete du fichier.
 */
export const findSsoProvider = async (email: string): Promise<string | null> => {
    const domain = domainOf(email);
    return domain ? (PROVIDER_BY_DOMAIN[domain] ?? null) : null;
};
